import CountryCodeSelector, {
  countries,
} from "../driverManagement/CountryCodeSelector"
import React, { useCallback, useEffect, useState } from "react"
import AdHocCommands from "../../api/adHocCommands"
import XMPPError from "@xmpp/error"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useTranslation } from "react-i18next"

interface AddEditUserModalProps {
  isOpen: boolean
  onClose: (event?: any) => void
  phoneNumber?: string
  firstName?: string
  lastName?: string
}

export const AddEditUserModal: React.FC<AddEditUserModalProps> = ({
  isOpen,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation()
  const [phoneNumberWithoutCode, setPhoneNumberWithoutCode] = useState(
    rest.phoneNumber ?? "",
  )
  const [firstName, setFirstName] = useState(rest.firstName ?? "")
  const [lastName, setLastName] = useState(rest.lastName ?? "")
  const [selectedDialCode, setSelectedDialCode] = useState("+48") // Default to Poland

  const handleClose = useCallback(() => {
    setPhoneNumberWithoutCode("")
    setSelectedDialCode("+48")
    setFirstName("")
    setLastName("")
    onClose()
  }, [onClose])

  useEffect(() => {
    if (rest.phoneNumber) {
      // Find matching country code
      const matchingCountry = countries.find((country) =>
        rest.phoneNumber?.startsWith(country.dialCode),
      )

      if (matchingCountry) {
        setSelectedDialCode(matchingCountry.dialCode)
        setPhoneNumberWithoutCode(
          rest.phoneNumber.substring(matchingCountry.dialCode.length),
        )
      } else {
        setPhoneNumberWithoutCode(rest.phoneNumber)
      }
    } else {
      setPhoneNumberWithoutCode("")
    }
    setFirstName(rest.firstName ?? "")
    setLastName(rest.lastName ?? "")
  }, [rest.phoneNumber, rest.firstName, rest.lastName])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await AdHocCommands.addExternalContact({
        client: window.XMPP!,
        phoneNumber: `${selectedDialCode}${phoneNumberWithoutCode}`,
        firstName: firstName,
        lastName: lastName,
      })
      handleClose()
    } catch (e) {
      const error = (e as XMPPError).text
      if (error === "Invalid phone number") {
        ;(
          window.document.getElementById("phone") as HTMLInputElement | null
        )?.select()
      }

      window.appToaster.show({
        message: error,
        intent: "danger",
        timeout: 2000,
      })
    }
  }

  const isEdit = rest.phoneNumber !== undefined

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {isEdit
              ? t("fleet.externalContact.titleEdit")
              : t("fleet.externalContact.titleAdd")}
          </DialogTitle>
          {!isEdit && (
            <p className="mt-2 text-sm text-muted-foreground">
              {t("fleet.externalContact.subtitle")}
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">{t("fleet.form.firstName")}</Label>
              <Input
                id="first_name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jan"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">{t("fleet.form.lastName")}</Label>
              <Input
                id="last_name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Kowalski"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("fleet.form.phoneNumber")}</Label>
              <div className="flex gap-2">
                <CountryCodeSelector
                  dialCode={selectedDialCode}
                  onChange={(country) => setSelectedDialCode(country.dialCode)}
                />
                <Input
                  id="phone"
                  required
                  value={phoneNumberWithoutCode}
                  onChange={(e) =>
                    setPhoneNumberWithoutCode(
                      e.target.value.replace(/[^\d\s]/g, ""),
                    )
                  }
                  placeholder="682 871 329"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              {isEdit ? t("common.save") : t("common.add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
